import { useCallback, useEffect, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { TopHeader, type TopHeaderProps } from './TopHeader';
import { Sidebar, type SidebarProps } from './Sidebar';
import { Breadcrumbs } from './Breadcrumbs';
import { CommandPalette } from './CommandPalette';
import './rahmen.css';

/**
 * Der Rahmen: Kopfzeile + Seitenleiste + Brotkrumen + Inhalt
 * (Auftrag Abschnitte 3-6).
 *
 * ═══ WARUM DER KONTEXT UNVERÄNDERT WEITERGEREICHT WIRD ══════════════════
 *
 * Lehre aus dem Vorfall vom 17.09.2026 (`ElektroRahmen.tsx`): Eine
 * Layoutroute ist im Router eine ECHTE Ebene, auch ohne eigenen Pfad.
 * `useOutletContext` gilt nur für die nächste Ebene - wer hier den Kontext
 * einliest und nicht unverändert an den eigenen `<Outlet context={...} />`
 * weitergibt, macht jede Seite darunter weiß. `tsc` und der volle Testsatz
 * blieben damals grün; gefunden hat es erst der Seitenaufruf.
 *
 * `AppShell` kennt den Inhalt des Kontexts deshalb absichtlich nicht (kein
 * Typ-Parameter, kein Auspacken) - er reicht ihn nur durch. Dasselbe Muster
 * wie in `seiten/Fremdmodelle.tsx`. Zwei Tests sichern es ab:
 * `AppShell.test.tsx` (Kontext von oben) und `rahmen-integration.test.tsx`
 * (die echte Verschachtelung AppShell → Projektrahmen → Seite).
 *
 * ═══ WARUM KOPF UND SEITENLEISTE IHRE DATEN ÜBER PROPS BEKOMMEN ═════════
 *
 * Nicht aus dem Router-Kontext: So bleibt AppShell unabhängig davon, was
 * eine künftige Elternroute dort hineinlegt, und die Kopplung steht im
 * Typsystem statt in einer Struktur, die AppShell erraten müsste.
 */
export interface AppShellProps {
  kopf: TopHeaderProps;
  seitenleiste: SidebarProps;
  /** Anzeigename des Projekts für die erste Brotkrume. */
  projektName?: string | null;
}

export function AppShell({ kopf, seitenleiste, projektName }: AppShellProps) {
  const kontext = useOutletContext();
  const [paletteOffen, setPaletteOffen] = useState(false);

  const paletteUmschalten = useCallback(() => setPaletteOffen((v) => !v), []);
  const paletteSchliessen = useCallback(() => setPaletteOffen(false), []);

  // Strg/Cmd+K (Auftrag Abschnitt 29). Bewusst mit `preventDefault`: In
  // Firefox liegt auf Strg+K die Adressleistensuche, und die Palette wäre
  // sonst nicht erreichbar. Andere Browserkürzel werden nicht angetastet.
  useEffect(() => {
    function beiTaste(ereignis: KeyboardEvent) {
      if ((ereignis.ctrlKey || ereignis.metaKey) && ereignis.key.toLowerCase() === 'k') {
        ereignis.preventDefault();
        paletteUmschalten();
      }
    }
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, [paletteUmschalten]);

  return (
    <div className="app-rahmen">
      {/* Erster Tabstopp der Seite: der Sprung über die Navigation hinweg
          (Abschnitt 28, Tastaturnavigation). Sichtbar nur bei Fokus. */}
      <a className="sprungmarke" href="#inhalt">Zum Inhalt springen</a>

      <TopHeader {...kopf} onSucheOeffnen={paletteUmschalten} />

      <div className="rahmen-koerper">
        <Sidebar {...seitenleiste} />

        <main className="rahmen-inhalt" id="inhalt" tabIndex={-1}>
          <div className="rahmen-inhalt-spalte">
            <Breadcrumbs
              eintraege={seitenleiste.eintraege}
              projektId={seitenleiste.projektId}
              projektName={projektName}
            />
            <Outlet context={kontext} />
          </div>
        </main>
      </div>

      <CommandPalette
        offen={paletteOffen}
        onSchliessen={paletteSchliessen}
        eintraege={seitenleiste.eintraege}
        projektId={seitenleiste.projektId}
      />
    </div>
  );
}
